import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Lock,
  Mail,
  RefreshCw,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import { api } from '../../../api/client';
import { useFeature, upgradeHintFor } from '../../../lib/features';
import { toast } from '../../../lib/toast';

// Section: Custom email sender (Enterprise feature, customEmailSender flag).
//
// Three states render distinct UI:
//   1. Locked (no entitlement)
//   2. Unconfigured (user has no domain saved)
//   3. Configured — displays DNS records + verify status + delete button
export default function EmailSenderSection() {
  const unlocked = useFeature('customEmailSender');
  const qc = useQueryClient();

  const senderQuery = useQuery({
    queryKey: ['brand-email-sender'],
    queryFn: () => api.get('/api/me/brand-email-sender').then((r) => r.data),
    enabled: unlocked,
    retry: false,
  });

  const setupMutation = useMutation({
    mutationFn: (body) =>
      api.post('/api/me/brand-email-sender', body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brand-email-sender'] });
      toast.success('Domain registered — add the DNS records below, then click Verify');
    },
    onError: (err) => toast.error(err, 'Could not register the domain'),
  });

  const verifyMutation = useMutation({
    mutationFn: () =>
      api.post('/api/me/brand-email-sender/verify').then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['brand-email-sender'] });
      if (data?.sender?.validSigning) {
        toast.success('Verified — your custom from address is now live');
      } else {
        toast.info('Not verified yet — DNS records can take a few minutes to propagate');
      }
    },
    onError: (err) => toast.error(err, 'Verification failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete('/api/me/brand-email-sender'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brand-email-sender'] });
      toast.success('Custom sender removed — emails revert to RankOnAI default');
    },
    onError: (err) => toast.error(err, 'Could not remove'),
  });

  if (!unlocked) {
    return (
      <div className="card border-dashed border-2 border-gray-200 bg-gray-50/60">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Lock className="w-4 h-4" />
            </span>
            Custom email sender
            <span className="pill bg-gray-100 text-gray-500">Enterprise</span>
          </div>
        </div>
        <p className="text-sm text-ink-600">
          Send all RankOnAI emails — welcome, upgrade, refund, AutoPilot digests, alerts —
          from your own domain instead of <code>noreply@rankonai.com</code>. Full white-label
          deliverability with SPF + DKIM you control.
        </p>
        <p className="text-xs text-ink-400 mt-2">
          Upgrade required: {upgradeHintFor('customEmailSender')}.
        </p>
      </div>
    );
  }

  const sender = senderQuery.data?.sender;
  const dnsRecords = senderQuery.data?.dnsRecords;
  const isConfigured = !!sender?.domain;

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Send className="w-4 h-4" />
            </span>
            Custom email sender
            {sender?.validSigning && (
              <span className="pill bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="w-3 h-3" /> Active
              </span>
            )}
            {isConfigured && !sender?.validSigning && (
              <span className="pill bg-amber-50 text-amber-700">Pending verification</span>
            )}
          </div>
          {isConfigured && (
            <button
              type="button"
              className="btn-ghost text-xs text-rose-600 hover:text-rose-700"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Remove the custom sender? RankOnAI emails revert to noreply@rankonai.com immediately.',
                  )
                ) {
                  deleteMutation.mutate();
                }
              }}
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          )}
        </div>

        {senderQuery.isLoading ? (
          <p className="text-sm text-ink-400">Loading…</p>
        ) : !isConfigured ? (
          <SetupForm
            onSubmit={(body) => setupMutation.mutate(body)}
            pending={setupMutation.isPending}
          />
        ) : (
          <ConfiguredView
            sender={sender}
            dnsRecords={dnsRecords}
            onVerify={() => verifyMutation.mutate()}
            verifying={verifyMutation.isPending}
          />
        )}
      </div>
    </div>
  );
}

function SetupForm({ onSubmit, pending }) {
  const [form, setForm] = useState({
    domain: '',
    fromEmail: '',
    fromName: '',
    replyTo: '',
  });

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      domain: form.domain.trim().toLowerCase(),
      fromEmail: form.fromEmail.trim().toLowerCase(),
      fromName: form.fromName.trim(),
      replyTo: form.replyTo.trim().toLowerCase() || undefined,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-ink-500 mb-2">
        Register a domain you own with Mandrill, then add two TXT records at your DNS provider.
        Takes ~5 minutes total; SPF + DKIM verification can take up to 30 minutes after DNS
        propagation.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="text-xs text-ink-600 md:col-span-1">
          Sending domain
          <input
            className="input mt-1"
            placeholder="acme.com"
            value={form.domain}
            required
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-600 md:col-span-1">
          From email
          <input
            className="input mt-1"
            placeholder="hello@acme.com"
            type="email"
            value={form.fromEmail}
            required
            onChange={(e) => setForm({ ...form, fromEmail: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-600 md:col-span-1">
          From name
          <input
            className="input mt-1"
            placeholder="Acme Marketing"
            value={form.fromName}
            required
            maxLength={80}
            onChange={(e) => setForm({ ...form, fromName: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-600 md:col-span-3">
          Reply-to (optional)
          <input
            className="input mt-1"
            placeholder="support@acme.com"
            type="email"
            value={form.replyTo}
            onChange={(e) => setForm({ ...form, replyTo: e.target.value })}
          />
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="btn-primary"
          disabled={pending || !form.domain || !form.fromEmail || !form.fromName}
        >
          <Mail className="w-4 h-4" />
          {pending ? 'Registering…' : 'Register domain'}
        </button>
        <span className="text-xs text-ink-400">
          From email must end with <code>@&lt;domain&gt;</code>
        </span>
      </div>
    </form>
  );
}

function ConfiguredView({ sender, dnsRecords, onVerify, verifying }) {
  return (
    <div className="space-y-4">
      {/* Current config */}
      <div className="rounded-lg border border-gray-100 bg-gray-50/40 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-ink-500 mb-1">From</div>
            <div className="font-medium text-ink-800">
              {sender.fromName} &lt;{sender.fromEmail}&gt;
            </div>
          </div>
          {sender.replyTo && (
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-500 mb-1">Reply-to</div>
              <div className="font-medium text-ink-800">{sender.replyTo}</div>
            </div>
          )}
        </div>
      </div>

      {/* Verification status */}
      <div className="rounded-lg border border-gray-100 p-4 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <div className="font-medium text-ink-800">DNS verification</div>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={onVerify}
            disabled={verifying}
          >
            <RefreshCw className={`w-4 h-4 ${verifying ? 'animate-spin' : ''}`} />
            {verifying ? 'Checking…' : 'Verify now'}
          </button>
        </div>
        <StatusRow label="SPF" valid={sender.spfValid} />
        <StatusRow label="DKIM" valid={sender.dkimValid} />
        <StatusRow label="Overall" valid={sender.validSigning} />
        {sender.lastCheckedAt && (
          <p className="text-xs text-ink-400">
            Last checked {new Date(sender.lastCheckedAt).toLocaleString()}.
          </p>
        )}
        {sender.lastCheckError && (
          <p className="text-xs text-rose-600">
            <AlertCircle className="w-3 h-3 inline-block mr-1" />
            {sender.lastCheckError}
          </p>
        )}
        {sender.validSigning ? (
          <p className="text-sm text-emerald-700 mt-1">
            <CheckCircle2 className="w-4 h-4 inline-block mr-1" />
            Active — all your emails now come from {sender.fromEmail}.
          </p>
        ) : (
          <p className="text-xs text-ink-500 mt-1">
            Add the DNS records below at your DNS provider, then come back and click Verify.
            Propagation typically takes 5-30 minutes.
          </p>
        )}
      </div>

      {/* DNS records */}
      {dnsRecords && (
        <div className="rounded-lg border border-gray-100 p-4 space-y-3">
          <div className="font-medium text-ink-800">Add these two TXT records</div>
          <DnsRecordRow label="SPF" record={dnsRecords.spf} />
          <DnsRecordRow label="DKIM" record={dnsRecords.dkim} />
        </div>
      )}
    </div>
  );
}

function StatusRow({ label, valid }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {valid ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
      ) : (
        <XCircle className="w-4 h-4 text-ink-300" />
      )}
      <span className="text-ink-700">{label}</span>
      <span
        className={`text-xs ml-auto ${
          valid ? 'text-emerald-600' : 'text-ink-400'
        }`}
      >
        {valid ? 'Verified' : 'Pending'}
      </span>
    </div>
  );
}

function DnsRecordRow({ label, record }) {
  const copy = (text, what) => {
    navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  };
  return (
    <div className="rounded-md bg-gray-50/60 border border-gray-100 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          {label}
        </span>
        <span className="text-xs text-ink-400 font-mono">{record.type}</span>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-start gap-2">
          <span className="text-[10px] uppercase tracking-wide text-ink-400 w-12 flex-shrink-0 mt-1">
            Host
          </span>
          <code className="flex-1 font-mono text-xs text-ink-800 break-all bg-white border border-gray-100 rounded px-2 py-1">
            {record.host}
          </code>
          <button
            type="button"
            className="btn-ghost text-xs flex-shrink-0"
            onClick={() => copy(record.host, 'Host')}
          >
            <Copy className="w-3 h-3" />
          </button>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-[10px] uppercase tracking-wide text-ink-400 w-12 flex-shrink-0 mt-1">
            Value
          </span>
          <code className="flex-1 font-mono text-[10px] text-ink-700 break-all bg-white border border-gray-100 rounded px-2 py-1">
            {record.value}
          </code>
          <button
            type="button"
            className="btn-ghost text-xs flex-shrink-0"
            onClick={() => copy(record.value, 'Value')}
          >
            <Copy className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
